import { useAuth0 } from "@auth0/auth0-react";
import {Fragment, useState, useEffect} from "react";
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Route, Routes } from "react-router-dom";
import { PageLoader } from "./components/page-loader";
import { AuthenticationGuard } from "./components/authentication-guard";
import { LeaderboardContext } from "./contexts/leaderboard-context";
import { SiteVarsContext } from "./contexts/sitevars-context";
import { getLeaderboardResource } from "./services/message.service";
import { getSiteVarsResource } from "./services/sitevars.service";
import { AdminPage } from "./pages/admin-page";
import { CallbackPage } from "./pages/callback-page";
import { HomePage } from "./pages/home-page";
import { NotFoundPage } from "./pages/not-found-page";
import { ProfilePage } from "./pages/profile-page";
import { ProtectedPage } from "./pages/protected-page";
import { LeaderboardPage } from "./pages/leaderboard-page";
import { LeaderboardGraphPage } from "./pages/leaderboard-graph-page";
import { TransfersPage } from "./pages/transfers-page";
import { LoansPage } from "./pages/loans-page";
import { FinancesPage } from "./pages/finances-page";
import { SiteVariablesPage } from "./pages/sitevariables-page";
import { SiteVarsPage } from "./pages/site-vars-page";
import { UpdateWeekPage } from "./pages/update-week-page";
import { ScraperPage } from "./pages/scraper-page";
import { AuctionListPage } from "./pages/auction-list-page";
import { AuctionListPageSimple } from "./pages/auction-list-page-simple";
import { AuctionResultsPage } from "./pages/auction-results-page";
import { TeamAdminPage } from "./pages/team-admin-page";
import { ScoreBreakdownPage } from "./pages/score-breakdown-page";
import { ManageTransfersPage } from "./pages/manage-transfers-page";
import { ConfirmedTeamsPage } from "./pages/confirmed-teams-page";
import { RulesPage } from "./pages/rules-page";
import { StatisticsPage } from "./pages/statistics-page";
import { AuctionHelperPage } from "./pages/auction-helper-page";
import { TeamProjectionsPage } from "./pages/team-projections-page";

export const App = () => {
  const { isLoading } = useAuth0();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true); // New state for leaderboard loading
  const [siteVarsData, setSiteVarsData] = useState([]);
  const [isSiteVarsLoading, setIsSiteVarsLoading] = useState(true); // New state for siteVars loading

  const getSiteVars = async () => {
    const { data, error } = await getSiteVarsResource();
    if (data) {
      setSiteVarsData(data);
    }
    setIsSiteVarsLoading(false); // Set loading to false after data is fetched
  };

  useEffect(() => {
    let isMounted = true;

    const getLeaderboard = async () => {
      const { data, error } = await getLeaderboardResource();
      if (!isMounted) {return;}
      if (data) {
        setLeaderboardData(data);
      }
      setIsLeaderboardLoading(false); // Set loading to false after data is fetched
    };
    
    getLeaderboard();
    getSiteVars(); // Initial fetch
    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading || isSiteVarsLoading || isLeaderboardLoading) { // Check Auth0, siteVars, and leaderboard loading
    return (
      <div className="page-layout">
        <PageLoader />
      </div>
    );
  }
  
  const darkTheme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  return (
    <Fragment>
      <LeaderboardContext.Provider value={{leaderboardData, setLeaderboardData}}>
        <SiteVarsContext.Provider value={{siteVarsData, setSiteVarsData, refreshSiteVars: getSiteVars}}>
          <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Routes>
              <Route path="/" element={<LeaderboardPage />} />
              <Route
                path="/profile"
                element={<AuthenticationGuard component={ProfilePage} />}
              />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/leaderboard-graph" element={<LeaderboardGraphPage />} />
              <Route path="/statistics" element={<StatisticsPage />} />
              <Route path="/transfers" element={<TransfersPage />} />
              <Route path="/loans" element={<LoansPage />} />
              <Route path="/finances" element={<FinancesPage />} />
              <Route
                path="/protected"
                element={<AuthenticationGuard component={ProtectedPage} />}
              />
              <Route
                path="/admin"
                element={<AuthenticationGuard component={AdminPage} />}
              />
              <Route
                path="/admin/site-vars"
                element={<AuthenticationGuard component={SiteVarsPage} />}
              />
              <Route
                path="/admin/update-week"
                element={<AuthenticationGuard component={UpdateWeekPage} />}
              />
              <Route
                path="/admin/sitevariables"
                element={<AuthenticationGuard component={SiteVariablesPage} />}
              />
              <Route
                path="/admin/scraper"
                element={<AuthenticationGuard component={ScraperPage} />}
              />
              <Route
                path="/admin/team-admin"
                element={<AuthenticationGuard component={TeamAdminPage} />}
              />
              <Route
                path="/score-breakdown"
                element={<AuthenticationGuard component={ScoreBreakdownPage} />}
              />
              <Route
                path="/admin/manage-transfers"
                element={<AuthenticationGuard component={ManageTransfersPage} />}
              />
              <Route
                path="/admin/confirmed-teams"
                element={<AuthenticationGuard component={ConfirmedTeamsPage} />}
              />
              <Route path="/auction-list" element={<AuctionListPage />} />
              <Route path="/auction-list-simple" element={<AuctionListPageSimple />} />
              <Route path="/auction-results" element={<AuctionResultsPage />} />
              <Route path="/rules" element={<RulesPage />} />
              <Route path="/auction-helper" element={<AuctionHelperPage />} />
              <Route path="/projections" element={<TeamProjectionsPage />} />
              <Route path="/team-projections" element={<TeamProjectionsPage />} />
              <Route path="/callback" element={<CallbackPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ThemeProvider>
          </SiteVarsContext.Provider>
      </LeaderboardContext.Provider>
    </Fragment>
  );
};
